<template>
  <nav class="ui-navbar">
    <button
      class="ui-navbar__menu-button"
      type="button"
      :aria-label="isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'"
      :aria-expanded="String(isMobileMenuOpen)"
      :disabled="isMenuIconAnimating"
      @click="toggleMobileMenu"
    >
      <img
        class="ui-navbar__menu-icon"
        :src="menuIconSrc"
        alt=""
        aria-hidden="true"
      >
    </button>

    <div class="ui-navbar__desktop">
      <div class="ui-navbar__group">
        <!-- Main Links for all users -->
        <a
          href="/"
          class="ui-nav-link"
        >Home</a>
        <a
          href="/about"
          class="ui-nav-link"
        >About</a>
        <details
          ref="desktopMusicDropdown"
          class="ui-dropdown"
          @toggle="handleDesktopMusicToggle"
        >
          <summary class="ui-nav-link ui-dropdown__summary">
            <span>Music</span>
            <img
              class="ui-navbar__expand-icon"
              :src="desktopMusicExpandIconSrc"
              alt=""
              aria-hidden="true"
            >
          </summary>
          <div class="ui-dropdown__menu">
            <a
              href="/discography"
              class="ui-dropdown__item"
            >Discography</a>
          </div>
        </details>
        <a
          href="/saysomethingnice"
          class="ui-nav-link"
        >Say Something Nice</a>
        <template v-if="showBlogLink">
          <a
            href="/blog"
            class="ui-nav-link"
          >Blog</a>
        </template>
        
        <template v-if="showDashboardLink">
          <a
            href="/dashboard"
            class="ui-nav-link"
          >Dashboard</a>
        </template>
        <template v-if="showShopLink">
          <a
            href="/shop"
            class="ui-nav-link"
          >Shop</a>
        </template>
    
        <template v-if="showDemoLink">
          <a
            href="/demo"
            class="ui-nav-link"
          >Demo</a>
          <a
            href="/theme-demo"
            class="ui-nav-link"
          >Theme Demo</a>
          <a
            href="/demo-mkii"
            class="ui-nav-link"
          >Demo mkii</a>
          <a
            href="/interactive-experiments"
            class="ui-nav-link"
          >Interactive</a>
          <a
            v-if="showLayerExplodeLink"
            href="/layer-explode-demo"
            class="ui-nav-link"
          >Layer Explode</a>
        </template>
      </div>

      <div class="ui-navbar__group">
        <template v-if="isAuthenticated && showAuthLink">
          <!-- Show these when logged in -->
          <a
            href="/profile"
            class="ui-nav-link"
          >Profile</a>
          <button
            class="ui-button ui-button--primary ui-button--sm"
            @click="logout"
          >
            Sign Out
          </button>
        </template>
        <template v-else-if="showAuthLink">
          <!-- Show these when not logged in -->
          <a
            href="/login"
            class="ui-nav-link"
          >Sign In</a>
          <a
            href="/register"
            class="ui-nav-link"
          >Sign Up</a>
        </template>
      </div>
    </div>

    <div
      v-if="isMobileMenuVisible"
      :class="['ui-mobile-menu', { 'ui-mobile-menu--open': isMobileMenuOpen }]"
      :style="{ '--menu-fade-ms': `${menuAnimationDurationMs}ms` }"
      @click.self="closeMobileMenu"
    >
      <div
        class="ui-mobile-menu__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div class="ui-mobile-menu__links">
          <a
            href="/"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Home</a>
          <a
            href="/about"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >About</a>
          <div class="ui-mobile-menu__group">
            <button
              class="ui-nav-link ui-mobile-menu__group-title"
              type="button"
              :aria-expanded="String(isMobileMusicOpen)"
              aria-controls="mobile-music-submenu"
              @click="toggleMobileMusicGroup"
            >
              <span>Music</span>
              <img
                class="ui-mobile-menu__expand-icon"
                :src="mobileMusicExpandIconSrc"
                alt=""
                aria-hidden="true"
              >
            </button>
            <transition name="ui-mobile-expand">
              <div
                v-show="isMobileMusicOpen"
                id="mobile-music-submenu"
                class="ui-mobile-menu__submenu"
              >
                <a
                  href="/discography"
                  class="ui-nav-link ui-mobile-menu__sublink"
                  @click="closeMobileMenu"
                >Discography</a>
              </div>
            </transition>
          </div>
          <a
            href="/saysomethingnice"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Say Something Nice</a>

          <a
            v-if="showBlogLink"
            href="/blog"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Blog</a>
          <a
            v-if="showDashboardLink"
            href="/dashboard"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Dashboard</a>
          <a
            v-if="showShopLink"
            href="/shop"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Shop</a>

          <a
            v-if="showDemoLink"
            href="/demo"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Demo</a>
          <a
            v-if="showDemoLink"
            href="/theme-demo"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Theme Demo</a>
          <a
            v-if="showDemoLink"
            href="/demo-mkii"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Demo mkii</a>
          <a
            v-if="showDemoLink"
            href="/interactive-experiments"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Interactive</a>
          <a
            v-if="showLayerExplodeLink"
            href="/layer-explode-demo"
            class="ui-nav-link"
            @click="closeMobileMenu"
          >Layer Explode</a>

          <template v-if="isAuthenticated && showAuthLink">
            <a
              href="/profile"
              class="ui-nav-link"
              @click="closeMobileMenu"
            >Profile</a>
            <button
              class="ui-button ui-button--primary ui-button--sm ui-mobile-menu__logout"
              @click="logout"
            >
              Sign Out
            </button>
          </template>
          <template v-else-if="showAuthLink">
            <a
              href="/login"
              class="ui-nav-link"
              @click="closeMobileMenu"
            >Sign In</a>
            <a
              href="/register"
              class="ui-nav-link"
              @click="closeMobileMenu"
            >Sign Up</a>
          </template>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapState, mapActions } from 'vuex';

const menuFrameContext = require.context(
  '@/assets/images/animations/menu',
  false,
  /^\.\/frame_\d{4}\.png$/
);
const menuFrames = menuFrameContext
  .keys()
  .sort()
  .map((framePath) => menuFrameContext(framePath));

const expandFrameContext = require.context(
  '@/assets/images/animations/expand',
  false,
  /^\.\/frame_\d{4}\.png$/
);
const expandFrames = expandFrameContext
  .keys()
  .sort()
  .map((framePath) => expandFrameContext(framePath));

export default {
  name: "MainNavbar",
  data() {
    return {
      showAuthLink: process.env.VUE_APP_SHOW_AUTH_ROUTES === 'true',
      showBlogLink: process.env.VUE_APP_SHOW_BLOG_POST_ROUTES === 'true',
      showDemoLink: process.env.VUE_APP_SHOW_DEMO_ROUTES === 'true',
      showLayerExplodeLink:
        process.env.NODE_ENV === 'development' && process.env.VUE_APP_SHOW_DEMO_ROUTES === 'true',
      showShopLink: process.env.VUE_APP_SHOW_SHOP_ROUTES === 'true',
      showDashboardLink: process.env.VUE_APP_SHOW_DASHBOARD_ROUTES === 'true',
      isMobileMenuOpen: false,
      isMobileMenuVisible: false,
      menuFrames,
      currentMenuFrameIndex: 0,
      isMenuIconAnimating: false,
      menuFrameDelayMs: 8,
      firstMenuFrameIndex: 0,
      lastMenuFrameIndex: menuFrames.length - 1,
      expandFrames,
      isDesktopMusicOpen: false,
      currentDesktopExpandFrameIndex: 0,
      isDesktopExpandIconAnimating: false,
      isMobileMusicOpen: false,
      currentExpandFrameIndex: 0,
      isExpandIconAnimating: false,
      expandFrameDelayMs: 6,
      firstExpandFrameIndex: 0,
      lastExpandFrameIndex: expandFrames.length - 1,
      isUnmounted: false,
    }
  },
  computed: {
    // Map the authentication state from Vuex store
    ...mapState({
      token: state => state.auth.token,  // Assuming your auth token is in the `auth` module
    }),
    isAuthenticated() {
      return !!this.token; // Derived state to determine if the user is logged in
    },
    menuIconSrc() {
      return this.menuFrames[this.currentMenuFrameIndex] || this.menuFrames[0];
    },
    menuAnimationDurationMs() {
      const frameSteps = Math.max(0, this.lastMenuFrameIndex - this.firstMenuFrameIndex);
      return frameSteps * this.menuFrameDelayMs;
    },
    mobileMusicExpandIconSrc() {
      return this.expandFrames[this.currentExpandFrameIndex] || this.expandFrames[0];
    },
    desktopMusicExpandIconSrc() {
      return this.expandFrames[this.currentDesktopExpandFrameIndex] || this.expandFrames[0];
    },
  },
  watch: {
    $route() {
      this.closeMobileMenu();
      this.isDesktopMusicOpen = false;
      this.currentDesktopExpandFrameIndex = this.firstExpandFrameIndex;
      if (this.$refs.desktopMusicDropdown) {
        this.$refs.desktopMusicDropdown.open = false;
      }
    },
  },
  beforeUnmount() {
    this.isUnmounted = true;
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    document.body.classList.remove('ui-mobile-menu-open');
  },
  created() {
    this.checkAuthState()
      .catch((err) => console.error("[MainNavbar] Error checking auth state:", err));
  },
  mounted() {
    window.addEventListener('keydown', this.handleGlobalKeydown);
  },
  methods: {
    ...mapActions({
      checkAuthState: 'checkAuthState',
      performLogout: 'logout',
    }),
    wait(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    },
    waitForNextPaint() {
      return new Promise((resolve) => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(resolve);
        });
      });
    },
    async playMenuIconAnimation(shouldShowCloseIcon) {
      if (this.isMenuIconAnimating) {
        return;
      }

      const targetFrameIndex = shouldShowCloseIcon
        ? this.lastMenuFrameIndex
        : this.firstMenuFrameIndex;

      if (this.currentMenuFrameIndex === targetFrameIndex) {
        return;
      }

      this.isMenuIconAnimating = true;

      try {
        const direction = this.currentMenuFrameIndex < targetFrameIndex ? 1 : -1;

        while (this.currentMenuFrameIndex !== targetFrameIndex) {
          this.currentMenuFrameIndex += direction;
          await this.wait(this.menuFrameDelayMs);
          if (this.isUnmounted) {
            return;
          }
        }
      } finally {
        this.isMenuIconAnimating = false;
      }
    },
    async playExpandIconAnimation(shouldExpand) {
      if (this.isExpandIconAnimating) {
        return;
      }

      const targetFrameIndex = shouldExpand
        ? this.lastExpandFrameIndex
        : this.firstExpandFrameIndex;

      if (this.currentExpandFrameIndex === targetFrameIndex) {
        return;
      }

      this.isExpandIconAnimating = true;

      try {
        const direction = this.currentExpandFrameIndex < targetFrameIndex ? 1 : -1;

        while (this.currentExpandFrameIndex !== targetFrameIndex) {
          this.currentExpandFrameIndex += direction;
          await this.wait(this.expandFrameDelayMs);
          if (this.isUnmounted) {
            return;
          }
        }
      } finally {
        this.isExpandIconAnimating = false;
      }
    },
    async playDesktopExpandIconAnimation(shouldExpand) {
      if (this.isDesktopExpandIconAnimating) {
        return;
      }

      const targetFrameIndex = shouldExpand
        ? this.lastExpandFrameIndex
        : this.firstExpandFrameIndex;

      if (this.currentDesktopExpandFrameIndex === targetFrameIndex) {
        return;
      }

      this.isDesktopExpandIconAnimating = true;

      try {
        const direction = this.currentDesktopExpandFrameIndex < targetFrameIndex ? 1 : -1;

        while (this.currentDesktopExpandFrameIndex !== targetFrameIndex) {
          this.currentDesktopExpandFrameIndex += direction;
          await this.wait(this.expandFrameDelayMs);
          if (this.isUnmounted) {
            return;
          }
        }
      } finally {
        this.isDesktopExpandIconAnimating = false;
      }
    },
    async handleDesktopMusicToggle(event) {
      this.isDesktopMusicOpen = event.target.open;
      await this.playDesktopExpandIconAnimation(this.isDesktopMusicOpen);
    },
    async toggleMobileMusicGroup() {
      if (this.isExpandIconAnimating) {
        return;
      }

      this.isMobileMusicOpen = !this.isMobileMusicOpen;
      await this.playExpandIconAnimation(this.isMobileMusicOpen);
    },
    async toggleMobileMenu() {
      if (this.isMenuIconAnimating) {
        return;
      }

      if (this.isMobileMenuOpen) {
        await this.closeMobileMenu();
      } else {
        await this.openMobileMenu();
        await Promise.all([
          this.playMenuIconAnimation(true),
          this.wait(this.menuAnimationDurationMs),
        ]);
      }
    },
    async openMobileMenu() {
      this.isMobileMenuVisible = true;
      await this.$nextTick();
      await this.waitForNextPaint();
      if (this.isUnmounted) {
        return;
      }
      this.isMobileMenuOpen = true;
      document.body.classList.add('ui-mobile-menu-open');
    },
    async closeMobileMenu() {
      if (this.isMenuIconAnimating) {
        return;
      }

      if (!this.isMobileMenuVisible) {
        document.body.classList.remove('ui-mobile-menu-open');
        this.isDesktopMusicOpen = false;
        this.currentDesktopExpandFrameIndex = this.firstExpandFrameIndex;
        this.isMobileMusicOpen = false;
        this.currentExpandFrameIndex = this.firstExpandFrameIndex;
        this.currentMenuFrameIndex = this.firstMenuFrameIndex;
        return;
      }

      this.isMobileMenuOpen = false;
      await Promise.all([
        this.playMenuIconAnimation(false),
        this.wait(this.menuAnimationDurationMs),
      ]);
      if (this.isUnmounted) {
        return;
      }
      this.isDesktopMusicOpen = false;
      this.currentDesktopExpandFrameIndex = this.firstExpandFrameIndex;
      this.isMobileMusicOpen = false;
      this.currentExpandFrameIndex = this.firstExpandFrameIndex;
      this.isMobileMenuVisible = false;
      document.body.classList.remove('ui-mobile-menu-open');
    },
    handleGlobalKeydown(event) {
      if (event.key === 'Escape') {
        this.closeMobileMenu();
      }
    },
    logout() {
      this.closeMobileMenu();
      this.performLogout();
    },
  },
};
</script>
